<template>
    <div :class="wrapperClass">
        <label v-if="label" class="form-label-alt">
            <span>{{ label }}</span>
            <FieldTooltip :content="tooltipHtml"/>
        </label>
        <input
            :type="type"
            class="form-control"
            :class="{ 'is-invalid': isInvalid, 'is-invalid--submitted': hasError, 'form-control-readonly': readonly }"
            :value="modelValue"
            :placeholder="placeholder"
            :min="min"
            :step="step"
            :readonly="readonly || undefined"
            :required="!readonly && required || undefined"
            :data-parsley-recommended="!readonly && recommended ? 'true' : undefined"
            :data-parsley-validate-if-empty="!readonly && recommended ? 'true' : undefined"
            :data-parsley-no-focus="!readonly && recommended ? 'true' : undefined"
            @input="$emit('update:modelValue', $event.target.value)"
            @input.capture="$emit('input', $event)"
        >
        <div v-if="hasError" class="form-error">
            <div v-if="required">{{ errorMessage || 'Dieses Feld ist ein Pflichtfeld.' }}</div>
            <div v-else-if="recommended">Dieses Feld wird empfohlen auszufüllen</div>
        </div>
        <slot name="after"></slot>
    </div>
</template>

<script setup>
import {computed, inject, toRef} from 'vue';
import FieldTooltip from './FieldTooltip.vue';
import {useFieldTooltip} from '../useFieldTooltip';

const props = defineProps({
    modelValue: { type: [String, Number], default: '' },
    label: { type: String, default: '' },
    /** Field description key, @see FieldDescriptionService::GROUPS */
    tooltip: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    type: { type: String, default: 'text' },
    required: { type: Boolean, default: false },
    recommended: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    highlight: { type: Boolean, default: false },
    noMargin: { type: Boolean, default: false },
    min: { type: [Number, String], default: undefined },
    step: { type: [Number, String], default: undefined },
    errorMessage: { type: String, default: '' },
});

defineEmits(['update:modelValue', 'input']);

const submitted = inject('submitted', { value: false });
const tooltipHtml = useFieldTooltip(toRef(props, 'tooltip'));

const isEmpty = computed(() => {
    const val = props.modelValue;
    if (val === null || val === undefined) return true;
    if (typeof val === 'string') return val.trim() === '';
    if (typeof val === 'number') return val === 0;
    return !val;
});

/** Red outline — always visible when required + empty */
const isInvalid = computed(() => !props.readonly && props.required && isEmpty.value);

/** Recommended warning — shown after first submit attempt */
const isRecommendedEmpty = computed(() => !props.readonly && props.recommended && isEmpty.value);

/** Error/warning message — only after first submit attempt */
const hasError = computed(() => submitted.value && (isInvalid.value || isRecommendedEmpty.value));

const wrapperClass = computed(() => ({
    'form-highlight': props.required || props.recommended || props.highlight,
    'mb-3': !props.noMargin,
}));
</script>
